// src/components/ReadjudicacionMasiva/Documentos/TipoDocumentoSelector.js

import React, { useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Typography,
    Checkbox,
    Chip,
    CircularProgress,
    Box
} from '@mui/material';
import {
    Description as DescriptionIcon
} from '@mui/icons-material';
import { nodeApiPath } from '../../../utils/paths';

const useStyles = makeStyles((theme) => ({
    card: {
        height: '100%',
        position: 'relative',
        transition: 'all 0.3s ease',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8]
        }
    },
    cardSelected: {
        border: `2px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.primary.light + '10'
    },
    cardContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: theme.spacing(2),
        minHeight: 160
    },
    icon: {
        fontSize: 48,
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(1),
        fontSize: '0.95rem',
        wordBreak: 'break-word',
        hyphens: 'auto'
    },
    description: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem'
    },
    checkbox: {
        position: 'absolute',
        top: theme.spacing(1),
        right: theme.spacing(1)
    },
    countChip: {
        position: 'absolute',
        top: theme.spacing(1),
        left: theme.spacing(1)
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200
    },
    emptyBox: {
        textAlign: 'center',
        padding: theme.spacing(4),
        color: theme.palette.text.secondary
    }
}));

const TipoDocumentoSelector = ({ seleccionados, onChange, cantidades }) => {
    const classes = useStyles();
    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Cargar modelos de documentos de las categorías Readjudicación - Carta Poder (ID=19) y Readjudicación - Recibo (ID=20)
    useEffect(() => {
        const cargarModelos = async () => {
            try {
                setLoading(true);

                // Cargar escritos de ambas categorías de readjudicación por Id_Categoria
                const token = localStorage.getItem('SIGIF_JWT_TOKEN');
                const headers = { 'Authorization': `Bearer ${token}` };
                const [resCartaPoder, resRecibo] = await Promise.all([
                    fetch(nodeApiPath('escritos/por-categoria/19'), { headers }).then(r => r.json()),
                    fetch(nodeApiPath('escritos/por-categoria/20'), { headers }).then(r => r.json())
                ]);

                const modelos = [];

                // Extraer items de la respuesta (la API devuelve { ok, items })
                const cartaPoderItems = resCartaPoder?.items || resCartaPoder || [];
                const reciboItems = resRecibo?.items || resRecibo || [];

                // Procesar Carta Poder
                if (Array.isArray(cartaPoderItems)) {
                    cartaPoderItems
                        .filter(item => item.Activo !== false && item.Estado === 'PUBLICADO')
                        .forEach(item => {
                            modelos.push({
                                id: item.Id,
                                tipo: `CARTA_PODER_${item.Id}`,
                                tipoBase: 'CARTA_PODER',
                                nombre: item.Escrito || item.nombre || 'Carta Poder',
                                descripcion: item.Description || item.descripcion || 'Carta poder para representación legal',
                                habilitado: true
                            });
                        });
                }

                // Procesar Recibo
                if (Array.isArray(reciboItems)) {
                    reciboItems
                        .filter(item => item.Activo !== false && item.Estado === 'PUBLICADO')
                        .forEach(item => {
                            modelos.push({
                                id: item.Id,
                                tipo: `RECIBO_${item.Id}`,
                                tipoBase: 'RECIBO',
                                nombre: item.Escrito || item.nombre || 'Recibo',
                                descripcion: item.Description || item.descripcion || 'Recibo de documentación',
                                habilitado: true
                            });
                        });
                }

                setDocumentos(modelos);
            } catch (error) {
                console.error('Error al cargar modelos de documentos:', error);
                setDocumentos([]);
            } finally {
                setLoading(false);
            }
        };

        cargarModelos();
    }, []);

    const handleToggle = (tipo) => {
        const nuevaSeleccion = seleccionados.includes(tipo)
            ? seleccionados.filter(t => t !== tipo)
            : [...seleccionados, tipo];
        onChange(nuevaSeleccion);
    };

    if (loading) {
        return (
            <Box className={classes.loadingBox}>
                <CircularProgress />
            </Box>
        );
    }

    if (documentos.length === 0) {
        return (
            <Box className={classes.emptyBox}>
                <DescriptionIcon style={{ fontSize: 64, opacity: 0.5, marginBottom: 16 }} />
                <Typography variant="h6" gutterBottom>
                    No hay modelos de documentos disponibles
                </Typography>
                <Typography variant="body2">
                    Debe crear modelos de documentos en la sección "Documentos" con la categoría "READJUDICACION" para poder usarlos aquí.
                </Typography>
            </Box>
        );
    }

    return (
        <Grid container spacing={3}>
            {documentos.map((doc) => {
                const isSelected = seleccionados.includes(doc.tipo);
                const cantidad = cantidades?.[doc.tipo] || 0;

                return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={doc.tipo}>
                        <Card
                            className={`${classes.card} ${isSelected ? classes.cardSelected : ''}`}
                            elevation={isSelected ? 4 : 1}
                        >
                            <CardActionArea
                                onClick={() => doc.habilitado && handleToggle(doc.tipo)}
                                disabled={!doc.habilitado}
                            >
                                <Checkbox
                                    checked={isSelected}
                                    className={classes.checkbox}
                                    color="primary"
                                    disabled={!doc.habilitado}
                                />
                                {cantidad > 0 && (
                                    <Chip
                                        label={cantidad}
                                        size="small"
                                        color="primary"
                                        className={classes.countChip}
                                    />
                                )}
                                <CardContent className={classes.cardContent}>
                                    <DescriptionIcon className={classes.icon} />
                                    <Typography variant="h6" className={classes.title}>
                                        {doc.nombre}
                                    </Typography>
                                    <Typography variant="body2" className={classes.description}>
                                        {doc.descripcion}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
};

export default TipoDocumentoSelector;
